import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AdminAuthAPI from '../../api/admin/auth';
import PasswordInput from '../../components/PasswordInput';
import './AdminPasswordSetup.css'; // Reuse the same styling

const AdminPasswordReset = () => {
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Extract token from URL parameters - check multiple possible parameter names
    const tokenFromUrl = searchParams.get('token') || 
                         searchParams.get('password_reset_token') || 
                         searchParams.get('stytch_token_type') ||
                         searchParams.get('reset_token');
    
    console.log('All URL params:', Object.fromEntries(searchParams.entries()));
    
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
      console.log('Password reset token found:', tokenFromUrl.substring(0, 20) + '...');
    } else {
      setError('Invalid reset link. Please request a new password reset.');
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    // Validate password strength
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      setLoading(false);
      return;
    }

    try {
      // Use the same API call as password setup - they should work the same way
      const response = await AdminAuthAPI.completePasswordSetup(token, password);
      
      if (response.success) {
        setSuccess(true);
        
        // Store the admin token and user data
        localStorage.setItem('admin_token', response.access_token);
        localStorage.setItem('admin_user', JSON.stringify(response.admin_user));
        localStorage.setItem('admin_token_expires', Date.now() + (response.expires_in * 1000));
        
        // Redirect to admin dashboard after a short delay
        setTimeout(() => {
          navigate('/admin/dashboard');
        }, 2000);
      } else {
        setError(response.error || 'Password reset failed');
      }
    } catch (error) {
      console.error('Password reset error:', error);
      setError('Password reset failed. Please try again or request a new reset link.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="admin-password-setup">
        <div className="setup-container">
          <div className="setup-card">
            <div className="setup-header">
              <h1>Password Reset Complete!</h1>
              <div className="success-icon">✅</div>
            </div>
            <div className="setup-content">
              <p>Your admin password has been reset successfully.</p>
              <p>Redirecting you to the admin dashboard...</p>
              <div className="loading-spinner"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-password-setup">
      <div className="setup-container">
        <div className="setup-card">
          <div className="setup-header">
            <h1>Reset Your Admin Password</h1>
            <p>Enter your new password below</p>
          </div>
          
          {/* Debug info - only show in development */}
          {process.env.NODE_ENV === 'development' && (
            <div style={{
              background: '#f0f0f0', 
              padding: '10px', 
              margin: '10px 0', 
              fontSize: '12px', 
              borderRadius: '4px'
            }}>
              <strong>Debug Info:</strong><br/>
              Token found: {token ? 'Yes (' + token.substring(0, 20) + '...)' : 'No'}<br/>
              URL: {window.location.href}<br/>
              All URL params: {JSON.stringify(Object.fromEntries(searchParams.entries()))}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="setup-form">
            {error && (
              <div className="error-message">
                <span className="error-icon">⚠️</span>
                {error}
              </div>
            )}
            
            <div className="form-group">
              <label htmlFor="password">New Password</label>
              <PasswordInput
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Enter your new password"
                className="form-input"
                minLength="8"
                autoComplete="new-password"
                showStrengthIndicator={true}
                userInputs={[]} // Could add admin email if available
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <PasswordInput
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="Confirm your new password"
                className="form-input"
                minLength="8"
                autoComplete="new-password"
                confirmValue={password}
                showConfirmationStatus={true}
              />
            </div>

            <button
              type="submit"
              disabled={loading || !token}
              className="setup-button"
            >
              {loading ? (
                <>
                  <span className="loading-spinner"></span>
                  Resetting Password...
                </>
              ) : (
                'Reset Password'
              )}
            </button>
          </form>

          <div className="setup-footer">
            <p>
              Need help? <a href="/admin/login">Back to Login</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPasswordReset; 